import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { useEffect, useRef, useState } from 'react';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import {
  desktopBreakpoint,
  mobileBreakpoint,
  tabletBreakpoint,
} from '@/src/domains/shared/breakpoints';
import { Label, StepTitle } from '@/src/views/ContractorManagement/StepsProgressBar';
import {
  ProgressBarSectionWrapper,
  ProgressBarSectionHeader,
  ProgressBarContainer as ProgressBarContainerComponent,
  NumberLineSpan as NumberLineSpanComponent,
  StepCard as StepCardComponent,
  ImageIconWrapper,
} from '@/src/views/ContractorManagement/styles';

export const ProgressBarContainer = styled(ProgressBarContainerComponent)`
  max-width: 1000px;

  ${desktopBreakpoint(css`
    top: 260px;
  `)}
`;

export const NumberLineSpan = styled(NumberLineSpanComponent)`
  width: 33%;

  ${desktopBreakpoint(css`
    ${fluidProp('padding-bottom', '230px', '215px')}
  `)}

  ${tabletBreakpoint(css`
    ${fluidProp('padding-bottom', '250px', '200px')}
  `)}

  &:after {
    width: 92%;
    left: -46%;

    ${desktopBreakpoint(css`
      width: 3px;
      left: 49.5%;
      ${fluidProp('height', '258px', '213px')}
      ${fluidProp('top', '-258px', '-213px')}
    `)}

    ${tabletBreakpoint(css`
      left: 49%;
      ${fluidProp('height', '260px', '213px')}
      ${fluidProp('top', '-260px', '-213px')}
    `)}

    ${mobileBreakpoint(css`
      left: 48%;
      ${fluidProp('height', '260px', '210px')}
      ${fluidProp('top', '-260px', '-210px')}
    `)}
  }
`;

export const ActiveNumberLineSpan = styled(NumberLineSpan)`
  & + ${NumberLineSpan}:before {
    border-color: var(--colors-turquoise);
    background: var(--colors-turquoise);
    color: var(--colors-irisBlue);
    ${sharedTransition('color, border-color, background', '0.6s')}
  }

  & + ${NumberLineSpan}:after {
    box-shadow: inset 333px 0 0 0 var(--colors-turquoise);
    ${sharedTransition('box-shadow', '0.6s')}
  }
`;

export const StepsCardsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 953px;
  width: 100%;

  ${desktopBreakpoint(css`
    flex-direction: column;
    align-items: center;
  `)}
`;

export const StepCard = styled(StepCardComponent)`
  max-width: 238px;

  ${tabletBreakpoint(css`
    max-width: 210px;
    margin-left: auto;
    margin-right: auto;
  `)}
`;

const TitleComponent = styled.div`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '32px', '42px')}
  color: var(--colors-blank);
  text-align: center;
  max-width: 650px;
  margin: 0 auto;
  ${fluidProp(['margin-bottom'], '46px', '90px')}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ProgressBar = styled.ul`
  list-style: none;
  counter-reset: step;
  padding-left: 0;

  ${desktopBreakpoint(css`
    display: flex;
    flex-direction: column;
    max-height: 50%;

    ${fluidProp('padding-top', '0px', '110px')}
  `)}
`;

const StepDescription = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-moonRaker);
  text-align: center;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const StepsProgressBar = ({
  label,
  steps,
  element,
  title,
  align,
  customTitle,
  ...props
}) => {
  const sectionRef = useRef(null);

  const [activeState, setActiveState] = useState(
    Array(steps.length)
      .fill(false)
      .map((val, i) => (i ? val : true))
  );

  useEffect(() => {
    [...Array(steps.length - 1).keys()].reduce(
      (acc, current) =>
        acc.then(
          () =>
            new Promise((done) => {
              const ratioReference = 0.3; // with 0.5 it doesn't work on small screens (like iPhone 5 or etc)
              const intersectionObserver = new IntersectionObserver(
                (entries) => {
                  if (entries[0].intersectionRatio < ratioReference) return;
                  setTimeout(() => {
                    setActiveState((prevActiveState) => {
                      const updatedActiveState = [...prevActiveState];
                      updatedActiveState[current + 1] = true;
                      return updatedActiveState;
                    });
                    done();
                  }, 2000);
                },
                {
                  threshold: ratioReference,
                }
              );
              intersectionObserver.observe(sectionRef?.current);
            })
        ),
      Promise.resolve()
    );
  }, [steps.length]);

  return (
    <ProgressBarSectionWrapper {...props} ref={sectionRef}>
      <ProgressBarSectionHeader>
        {label && <Label>{label}</Label>}
        {customTitle ? (
          <TitleComponent>
            <h2>{customTitle}</h2>
          </TitleComponent>
        ) : (
          <RichText render={title} Component={TitleComponent} />
        )}
      </ProgressBarSectionHeader>
      <ProgressBarContainer>
        <ProgressBar $align={align}>
          {[...Array(steps.length).keys()].map((_, index) =>
            activeState[index + 1] ? (
              <ActiveNumberLineSpan key={index} />
            ) : (
              <NumberLineSpan key={index} />
            )
          )}
        </ProgressBar>
      </ProgressBarContainer>

      <StepsCardsContainer>
        {steps.map(
          ({ illustration, step_title: stepTitle, step_description: stepDescription }, index) => (
            <StepCard $active={activeState[index]} key={index}>
              <ImageIconWrapper>
                <Image
                  src={illustration.url}
                  alt={illustration.alt || ''}
                  width={illustration.dimensions.width}
                  height={illustration.dimensions.height}
                  quality="100"
                />
              </ImageIconWrapper>
              <RichText render={stepTitle} Component={StepTitle} />
              <RichText render={stepDescription} Component={StepDescription} $align={align} />
            </StepCard>
          )
        )}
      </StepsCardsContainer>
    </ProgressBarSectionWrapper>
  );
};
